"use client";

import { Icon } from "@iconify-icon/react";
import Image from "next/image";
import { MigrationLink } from "@/common/components-2/migration-link";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { isNull } from "@/common/helpers/lang";
import { getColumnItemLinkClass, getCompanyMetaWrapperClass, getFooterClass, getTermsLinkClass } from "./footer.styles";
import type { FooterProps } from "./footer.types";

const companyMeta =
  "<p>House-Med S.A.<br>Kuklińskiego 17a, <br>30-720 Krak&oacute;w</p>\n<p>&nbsp;</p>\n<p>NIP: 6751403730 <br>KRS: 0000315649 <br>REGON: 120807570</p>";

export function Footer({ className, columns, termsLabel, termsUrl, socials, ...props }: FooterProps) {
  return (
    <div className={getFooterClass({ className })} {...props}>
      <div className="container flex max-w-[1200px] flex-col">
        <div className="mb-10 flex flex-col lg:mb-24 lg:flex-row lg:justify-between">
          <div className="order-last mt-15 lg:order-first lg:my-0 lg:mr-24">
            <div className="mb-6 lg:mb-20">
              <MigrationLink href="/">
                <Image src="/next-assets/logo.svg" alt="logo" width={140} height={24} />
              </MigrationLink>
            </div>
            <div className={getCompanyMetaWrapperClass()}>
              <SanitizedContent as="div" content={companyMeta} />
            </div>
            <div className="mt-8 flex gap-4">
              {socials.instagramUrl ? (
                <MigrationLink
                  href={socials.instagramUrl}
                  target="_blank"
                  aria-label="Instagram"
                  className="lg:hover:-translate-y-1 text-3xl duration-300 hover:text-primary"
                >
                  <Icon icon="mdi:instagram" />
                </MigrationLink>
              ) : null}
              {socials.facebookUrl ? (
                <MigrationLink
                  href={socials.facebookUrl}
                  target="_blank"
                  aria-label="Facebook"
                  className="lg:hover:-translate-y-1 text-3xl duration-300 hover:text-primary"
                >
                  <Icon icon="mdi:facebook" />
                </MigrationLink>
              ) : null}
              {socials.linkedinUrl ? (
                <MigrationLink
                  href={socials.linkedinUrl}
                  target="_blank"
                  aria-label="LinkedIn"
                  className="lg:hover:-translate-y-1 text-3xl duration-300 hover:text-primary"
                >
                  <Icon icon="mdi:linkedin" />
                </MigrationLink>
              ) : null}
            </div>
          </div>
          {columns.map((column, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Ok
            <div key={`footer-nav-column-${index}`} className="mt-12 lg:mt-0">
              {isNull(column.label) ? (
                <span className="opacity-0">.</span>
              ) : (
                <h3 className="font-bold text-gray-400">{column.label}</h3>
              )}
              <ul className="mt-5 flex flex-col gap-6">
                {column.items.map((item, itemIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: index is the only unique identifier
                  <li key={`footer-nav-item-${itemIndex}`}>
                    <MigrationLink className={getColumnItemLinkClass()} href={item.url}>
                      {item.label}
                    </MigrationLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-3 py-6 text-center">
          <MigrationLink className={getTermsLinkClass()} href={termsUrl}>
            {termsLabel}
          </MigrationLink>
        </div>
      </div>
      <div className="border-t py-5">
        <div className="container max-w-[1520px] opacity-80">
          <span className="order-last text-sm lg:w-2/5">© {new Date().getFullYear()} uPacjenta</span>
        </div>
      </div>
    </div>
  );
}
